/**
 * First-game coach-marks (UX-SPEC §5 "Onboarding"): three tips in sequence,
 * each anchored to what it talks about — the rack, the ★ and the preview.
 * They never block input: only the bubble itself takes pointer events, focus
 * is never moved, and placing tiles advances them automatically. Dismissing
 * (Skip, Escape, or finishing) is remembered in localStorage.
 *
 * Screen readers hear each tip's text once through <LiveAnnouncer/> (a live
 * region mounted together with its text is often not read, and wrapping the
 * buttons in one made them read "Skip Next" too). The bubble steps out of the
 * way while it would cover the keyboard focus (WCAG 2.4.11).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import clsx from 'clsx';
import { CENTER, isBoardEmpty, type Board } from '@wordloom/engine';
import { useReducedMotion } from '../hooks/useMediaQuery';
import { useDraftStore } from '../state/draftStore';
import { ONBOARDING_STEPS, useUiStore } from '../state/uiStore';
import { IconClose } from './Icons';
import './CoachMarks.css';

interface Step {
  anchor: string;
  title: string;
  body: string;
}

export const COACH_STEPS: readonly Step[] = [
  {
    anchor: '[data-coach="rack"]',
    title: 'Your tiles',
    body: 'Drag tiles onto the board — or tap a tile, then a square.',
  },
  {
    anchor: `[data-cell="${CENTER},${CENTER}"]`,
    title: 'The first word',
    body: 'Your first word must cross the ★.',
  },
  {
    anchor: '[data-coach="preview"]',
    title: 'Score it',
    body: 'Check the points here, then press Play.',
  },
];

/**
 * Which step to show given progress: the ★ tip only makes sense on an empty
 * board, and once a tile covers the ★ it has done its job. Pure (tested).
 */
export function effectiveStep(
  step: number,
  board: Board,
  placements: readonly { row: number; col: number }[],
): number {
  let s = step;
  if (s === 0 && placements.length > 0) s = 1;
  if (s === 1) {
    const coversStar = placements.some((p) => p.row === CENTER && p.col === CENTER);
    if (!isBoardEmpty(board) || coversStar) s = 2;
  }
  return Math.min(s, ONBOARDING_STEPS - 1);
}

interface Placement {
  top: number;
  left: number;
  arrowX: number;
  side: 'above' | 'below';
}

const BUBBLE_W = 280;
const GAP = 12;
const MARGIN = 12;

function measure(anchor: Element, bubble: HTMLElement): Placement | null {
  const r = anchor.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  const vw = document.documentElement.clientWidth;
  const vh = window.innerHeight;
  const h = bubble.offsetHeight || 120;
  const w = Math.min(BUBBLE_W, vw - MARGIN * 2);
  const side: Placement['side'] = r.top - h - GAP >= MARGIN || r.bottom + h + GAP > vh ? 'above' : 'below';
  const top = side === 'above' ? r.top - h - GAP : r.bottom + GAP;
  const cx = r.left + r.width / 2;
  const left = Math.max(MARGIN, Math.min(vw - w - MARGIN, cx - w / 2));
  return { top: Math.max(MARGIN, top), left, arrowX: Math.max(18, Math.min(w - 18, cx - left)), side };
}

function samePlacement(a: Placement | null, b: Placement | null): boolean {
  if (a === null || b === null) return a === b;
  return a.top === b.top && a.left === b.left && a.arrowX === b.arrowX && a.side === b.side;
}

/** The focused element sits (partly) under the bubble. */
function coversFocus(bubble: HTMLElement | null): boolean {
  const focused = document.activeElement;
  if (!bubble || !(focused instanceof HTMLElement) || focused === document.body || bubble.contains(focused)) return false;
  const a = bubble.getBoundingClientRect();
  const b = focused.getBoundingClientRect();
  return a.left < b.right && b.left < a.right && a.top < b.bottom && b.top < a.bottom;
}

export function CoachMarks({ board }: { board: Board }) {
  const rawStep = useUiStore((s) => s.onboardingStep);
  const placements = useDraftStore((s) => s.placements);
  const reduced = useReducedMotion();
  const bubbleRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState<Placement | null>(null);
  const [coveringFocus, setCoveringFocus] = useState(false);

  // AnimatePresence keeps the previous tip mounted while it fades out, so for
  // a moment two bubbles share this ref. An object ref would be nulled when
  // the old one unmounts (after the new one attached), leaving the new tip
  // stuck invisible in `coach--measuring`. Clear it only if it still points at
  // the bubble that is going away (motion forwards React 19 ref cleanups).
  const setBubble = useCallback((el: HTMLDivElement | null) => {
    if (!el) return undefined;
    bubbleRef.current = el;
    return () => {
      if (bubbleRef.current === el) bubbleRef.current = null;
    };
  }, []);

  const step = rawStep === null ? null : effectiveStep(rawStep, board, placements);
  const def = step === null ? null : COACH_STEPS[step];

  // Each tip is read once, text only (the buttons are reachable in the region).
  useEffect(() => {
    if (step === null || !def) return;
    useUiStore.getState().announce(`Tip ${step + 1} of ${ONBOARDING_STEPS}: ${def.title}. ${def.body}`);
  }, [step, def]);

  // Escape dismisses the tips, like Skip. Listening on document runs before
  // the game's window-level shortcuts, and preventDefault stops the same key
  // from also recalling the draft. Dialogs, the letter picker and a drag
  // handle their own Escape first (and prevent it).
  useEffect(() => {
    if (!def) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      useUiStore.getState().dismissOnboarding();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [def]);

  // Never cover the keyboard focus: hide while the focused control is under
  // the bubble, show again once focus moves elsewhere (or the bubble moves).
  useEffect(() => {
    if (!def) return;
    const check = () => setCoveringFocus(coversFocus(bubbleRef.current));
    const onFocusOut = (e: FocusEvent) => {
      if (!e.relatedTarget) setCoveringFocus(false);
    };
    check();
    document.addEventListener('focusin', check);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('focusin', check);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [def, pos]);

  // Persist automatic progress so a re-render with no placements doesn't go back.
  useEffect(() => {
    if (rawStep !== null && step !== null && step > rawStep) {
      const ui = useUiStore.getState();
      for (let i = rawStep; i < step; i++) ui.advanceOnboarding();
    }
  }, [rawStep, step]);

  useLayoutEffect(() => {
    if (!def) return;
    let frame = 0;
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const anchor = document.querySelector(def.anchor);
        const bubble = bubbleRef.current;
        const next = anchor && bubble ? measure(anchor, bubble) : null;
        setPos((prev) => (samePlacement(prev, next) ? prev : next));
      });
    };
    update();
    // Layout settles (fonts, the board zoom, sheets): keep following the anchor.
    const interval = window.setInterval(update, 500);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(frame);
      window.clearInterval(interval);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [def]);

  if (step === null || !def) return null;
  const last = step >= ONBOARDING_STEPS - 1;
  const ui = useUiStore.getState();

  return createPortal(
    <AnimatePresence>
      <motion.div
        key={step}
        ref={setBubble}
        className={clsx('coach', pos ? `coach--${pos.side}` : 'coach--measuring', coveringFocus && 'coach--yield')}
        style={pos ? { top: pos.top, left: pos.left, ['--arrow-x' as string]: `${pos.arrowX}px` } : undefined}
        role="region"
        aria-label={`Tip ${step + 1} of ${ONBOARDING_STEPS}`}
        initial={{ opacity: 0, y: 6, scale: 0.97 }}
        animate={{ opacity: pos ? 1 : 0, y: 0, scale: 1 }}
        exit={{ opacity: 0 }}
        // Reduced motion: MotionConfig drops the lift, and fades stay ≤ 120 ms.
        transition={{ duration: reduced ? 0.12 : 0.2 }}
      >
        <div className="coach__head">
          <span className="coach__step">
            {step + 1}/{ONBOARDING_STEPS}
          </span>
          <strong className="coach__title">{def.title}</strong>
          <button type="button" className="btn btn--icon btn--ghost btn--sm coach__close" aria-label="Skip tips" onClick={() => ui.dismissOnboarding()}>
            <IconClose />
          </button>
        </div>
        <p className="coach__body">{def.body}</p>
        <div className="coach__foot">
          <span className="coach__dots" aria-hidden="true">
            {COACH_STEPS.map((_, i) => (
              <i key={i} className={clsx(i === step && 'is-on')} />
            ))}
          </span>
          {!last && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => ui.dismissOnboarding()}>
              Skip
            </button>
          )}
          <button type="button" className="btn btn--primary btn--sm" onClick={() => ui.advanceOnboarding()}>
            {last ? 'Got it' : 'Next'}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body,
  );
}
